import { useCallback, useMemo, useState } from "react"
import IInvoice from "~/interfaces/IInvoice"
import { validElectric } from "~/types"
import api from "~/api2"
import { mapLineItemToParams } from "~/api2/orders"
import { PAYMENT_MODES } from "~/constants"
import IOrderPreview from "~/interfaces/IOrderPreview"

interface PriceDetails {
  loadingIntent: boolean
  upsertIntent: (
    paymentMethod: string,
    notifyAdmin?: boolean,
    isSavingPaymentMethod?: boolean,
    isAutoCharging?: boolean,
  ) => Promise<{ id: string; clientSecret: string }>
}

const useBuildPriceDetails = (invoice: IInvoice): PriceDetails => {
  const [loadingIntent, setIsLoadingIntent] = useState(false)
  const buildOrderDetails = useCallback(
    (paymentMethod: string, isSavingPaymentMethod?: boolean, isAutoCharging?: boolean) => ({
      order_id: invoice.order_id,
      camp_id: invoice.order.camp_id,
      camper_id: invoice.order.camper_id,
      camping_style_id: invoice.reservation?.site?.camping_style_id!,
      site_id: invoice.reservation?.site_id!,
      start_date: invoice.reservation?.start_date!,
      end_date: invoice.reservation?.end_date!,
      user_electric: invoice.reservation?.camper?.vehicle_electric as validElectric,
      user_passengers: invoice.reservation?.passengers ?? 0,
      user_kid_campers: invoice.reservation?.kid_campers ?? 0,
      number_of_pets: Number(invoice.reservation?.pets_info) ?? 0,
      user_rig_year: invoice.reservation?.camper?.vehicle_year,
      interval_id: invoice.reservation?.selected_booking_interval_id,
      is_service_fee_applied: true,
      is_tax_enabled: true,
      is_passenger_fees_enabled: true,
      is_pets_fee_enabled: true,
      is_holiday_rate_enabled: true,
      is_weekend_fee_enabled: true,
      is_credit_card_fee_enabled: true,
      discount_slug: invoice.reservation.discount_type,
      is_terminal_payment: false,
      payment_mode: PAYMENT_MODES.INVOICE,
      selected_payment_method: paymentMethod,
      line_items_attributes: invoice.order.line_items.map((li) => mapLineItemToParams(li, Number(li.tax_rate) > 0)),
      is_storing_credit_card_enabled: isSavingPaymentMethod,
      is_auto_charging_enabled: isAutoCharging,
    }),
    [invoice],
  )

  const upsertIntent = async (
    paymentMethod: string,
    notifyAdmin = false,
    isSavingPaymentMethod?: boolean,
    isAutoCharging?: boolean,
  ) => {
    setIsLoadingIntent(true)

    const orderDetails = buildOrderDetails(paymentMethod, isSavingPaymentMethod, isAutoCharging)
    const resp = await api.Orders.createPaymentIntent({ ...(orderDetails as any), notify_admin: notifyAdmin })

    if (!resp.payment_intent.client_secret) throw new Error("Missing client secret from payment intent response")

    setIsLoadingIntent(false)
    return { id: resp.payment_intent.id, clientSecret: resp.payment_intent.client_secret }
  }

  return {
    loadingIntent,
    upsertIntent,
  }
}

export default useBuildPriceDetails
